// Pure helpers for shopper category navigation (#63). No React, no I/O.

import type { Category } from '../shared/types';

// Emoji per L1 department, keyed by the German department name. Static map (no
// icon library). Falls back to a generic basket for anything unmapped.
export const DEPARTMENT_EMOJI: Record<string, string> = {
  'Frische & Kühlung': '🥛',
  'Tiefkühl': '❄️',
  'Backwaren & Cerealien': '🍞',
  'Süßwaren & Snacks': '🍫',
  'Konserven & Trockenware': '🥫',
  'Getränke': '🥤',
  'Haushalt & Reinigung': '🧹',
  'Körperpflege & Beauty': '🧴',
  'Baby & Kind': '🍼',
  'Tiernahrung': '🐾',
};

export function emojiForDepartment(name: string): string {
  return DEPARTMENT_EMOJI[name] ?? '🛒';
}

const bySortThenName = (a: Category, b: Category) =>
  (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.name).localeCompare(String(b.name));

// L1 departments, ordered by sortOrder then name.
export function l1Departments(categories: Category[]): Category[] {
  return categories.filter((c) => c.level === 1).slice().sort(bySortThenName);
}

// L2/L3 sub-categories within a department (path[0] === l1Id), ordered so each
// L2 is followed by its L3 children (sort by the full path).
export function subcategoriesOf(categories: Category[], l1Id: string): Category[] {
  return categories
    .filter((c) => (c.level === 2 || c.level === 3) && (c.path?.[0] === l1Id))
    .slice()
    .sort((a, b) => (a.path ?? []).join('/').localeCompare((b.path ?? []).join('/')) || bySortThenName(a, b));
}

// The L1 department id that a selected category belongs to (its path root).
export function l1IdOf(categories: Category[], selectedId: string): string | null {
  const c = categories.find((x) => x.id === selectedId);
  return c?.path?.[0] ?? (c?.level === 1 ? c.id : null);
}

// Breadcrumb trail for a selected category, built straight from its `path` — no
// recursive lookups (AC5). Returns [] when the id/path is unknown.
export function breadcrumbFor(categories: Category[], selectedId: string): Array<{ id: string; name: string }> {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const selected = byId.get(selectedId);
  const path = selected?.path ?? (selected ? [selected.id] : []);
  return path.map((id) => ({ id, name: byId.get(id)?.name ?? id }));
}
